import { BoxSelect, Route } from "lucide-react";

import { Kbd, KbdHint } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { t } from "../../i18n/i18n";
import { DrawingBanner } from "../graphics/DraftBanner";
import type { BannerLayout } from "../manoeuvre/freeBands";
import { FOOTPRINT_ERRORS, type FootprintError } from "./footprint";
import { useZoneDraw, type ZoneDrawing } from "./zoneDraw";
import { useLastDefined } from "../../components/presence";

/** Bandeau du tracé de la zone libre (docs/14 §2.9 « Axe ») : « Cliquez l'arrière du fuseau », puis « Cliquez
 *  l'extrémité côté ennemi · Échap annule » ; pour une extension, les deux coins de l'emprise. Au doigt, « Touchez ».
 *  Un clic refusé dit sa règle (en rouge d'erreur) ; le tracé continue. Même gabarit que le bandeau des graphiques. */
export function ZoneDrawBanner({ onCancel, layout = "full" }: { onCancel: () => void; layout?: BannerLayout }) {
  const coarse = useMediaQuery("(pointer: coarse)");
  // État du tracé lu ici (et non par la carte) : seul ce bandeau léger suit les mouvements du pointeur.
  const current = useZoneDraw((s) => s.drawing);
  const drawing = useLastDefined(current) as ZoneDrawing | null | undefined;
  if (!drawing) return null;
  const first = drawing.points.length === 0;
  const axis = drawing.mode === "axis";
  const hint = axis
    ? first
      ? coarse
        ? t("Touchez l'arrière du fuseau.")
        : t("Cliquez l'arrière du fuseau.")
      : coarse
        ? t("Faites glisser la carte : l'aperçu suit le réticule. Touchez l'extrémité côté ennemi.")
        : t("Cliquez l'extrémité côté ennemi.")
    : first
      ? coarse
        ? t("Touchez un coin de l'emprise à ajouter.")
        : t("Cliquez un coin de l'emprise à ajouter.")
      : coarse
        ? t("Faites glisser la carte : l'aperçu suit le réticule. Touchez le coin opposé, l'emprise recouvrant une partie du terrain.")
        : t("Cliquez le coin opposé : l'emprise recouvre une partie du terrain.");
  const error = drawing.error && drawing.error in FOOTPRINT_ERRORS ? t(FOOTPRINT_ERRORS[drawing.error as FootprintError]) : undefined;
  return (
    <DrawingBanner
      layout={layout}
      icon={axis ? <Route size={16} className="shrink-0 text-ink-soft" aria-hidden /> : <BoxSelect size={16} className="shrink-0 text-ink-soft" aria-hidden />}
      title={axis ? t("Axe du terrain libre") : t("Étendre le terrain")}
      hint={
        <>
          {hint}
          {!coarse && (
            <KbdHint className="ml-1 whitespace-nowrap">
              <Kbd>{t("Échap")}</Kbd> {t("annule.")}
            </KbdHint>
          )}
        </>
      }
      error={error}
      onCancel={onCancel}
    />
  );
}
